/**
 * testimonial.js
 * Shopify Hyper Theme 2.0 — Testimonials Section
 *
 * Accessibility target: WCAG 2.2 AA
 * Screen reader support: NVDA, JAWS, VoiceOver, TalkBack
 *
 * Key fixes applied to TestimonialLayered:
 *  - Trigger element promoted to a semantic <button> via ARIA if it isn't one
 *  - aria-expanded reflects open/closed state at all times
 *  - aria-controls links trigger → content panel (unique IDs)
 *  - Content panel carries role="region" + aria-labelledby pointing back to trigger
 *  - Hidden content is removed from the AT tree via aria-hidden + inert
 *  - Full keyboard support: Enter / Space to toggle, Escape to close
 *  - Hover mode is ADDITIVE — keyboard/focus still works independently
 *  - Outside-click closes with matching Escape-key close
 *  - disconnectedCallback typo fixed → memory leak resolved
 *  - All event listeners stored as named bound references for proper removal
 *  - No reliance on setTimeout for AT state changes (immediate aria updates)
 *  - Reduced-motion respected for any CSS transition side-effects
 */

/* ─────────────────────────────────────────────
   TestimonialsComponent (slider/grid orchestrator)
   Largely unchanged — only a11y focus fix added
───────────────────────────────────────────── */
if (!customElements.get('testimonials-component')) {
  class Testimonials extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.selectors = {
        sliderWrapper: '.testimonials__items',
        pagination: '.swiper-pagination',
        nextEl: '.swiper-button-next',
        prevEl: '.swiper-button-prev',
      };
      this.classes = {
        grid: 'f-grid',
        swiper: 'swiper',
        swiperWrapper: 'swiper-wrapper',
      };

      this.sectionId = this.dataset.sectionId;
      this.section = this.closest(`.section-${this.sectionId}`);
      this.sliderWrapper = this.querySelector(this.selectors.sliderWrapper);

      this.enableSlider = this.dataset.enableSlider === 'true';
      this.items = parseInt(this.dataset.items);
      this.laptopItems = parseInt(this.dataset.laptopItems);
      this.tabletItems = parseInt(this.dataset.tabletItems);
      this.layout = this.dataset.layout;
      this.swipeMobile = this.dataset.swipeMobile === 'true';

      this.sliderInstance = false;

      if (!this.enableSlider) return;

      this.init();

      this._onMatchMobile = () => this.init();
      this._onUnmatchMobile = () => this.init();
      document.addEventListener('matchMobile', this._onMatchMobile);
      document.addEventListener('unmatchMobile', this._onUnmatchMobile);
    }

    disconnectedCallback() {
      document.removeEventListener('matchMobile', this._onMatchMobile);
      document.removeEventListener('unmatchMobile', this._onUnmatchMobile);
    }

    get paginationType() {
      return this.dataset.paginationType;
    }

    init() {
      if (FoxTheme.config.mqlMobile && this.swipeMobile) {
        this.destroySlider();
      } else {
        this.initSlider();
      }
    }

    initSlider() {
      const columnGap = window.getComputedStyle(this.sliderWrapper).getPropertyValue('--f-column-gap');
      const columnGapMobile = window.getComputedStyle(this.sliderWrapper).getPropertyValue('--f-column-gap-mobile');
      const spaceBetween = parseFloat(columnGap.replace('rem', '')) * 10;
      const spaceBetweenMobile = parseFloat(columnGapMobile.replace('rem', '')) * 10;
      let additionModules = [];
      let slideToMethod = 'slideTo';

      this.defaultOptions = {
        slidesPerView: 1,
        spaceBetween: spaceBetween,
        navigation: {
          nextEl: this.section.querySelector(this.selectors.nextEl),
          prevEl: this.section.querySelector(this.selectors.prevEl),
        },
        pagination: {
          el: this.section.querySelector(this.selectors.pagination),
          type: this.paginationType || 'progressbar',
        },
        breakpoints: {
          768: {
            slidesPerView: this.tabletItems > 3 ? 3 : this.tabletItems,
          },
          1024: {
            slidesPerView: this.laptopItems > 3 ? 3 : this.laptopItems,
          },
          1280: {
            slidesPerView: this.items,
          },
        },
        loop: false,
        threshold: 2,
      };

      if (this.layout === 'focused') {
        slideToMethod = 'slideToLoop';
        this.settings = {
          centeredSlides: true,
          spaceBetween: spaceBetweenMobile,
          pagination: {
            el: this.section.querySelector(this.selectors.pagination),
            clickable: true,
            type: this.paginationType || 'progressbar',
          },
          loop: true,
          breakpoints: {
            768: {
              slidesPerView: 2,
              spaceBetween: spaceBetween,
            },
            1280: {
              slidesPerView: Math.max(2, this.getSlidesPerViewCentered(this.laptopItems)),
              spaceBetween: spaceBetween,
            },
            1440: {
              slidesPerView: Math.max(2, this.getSlidesPerViewCentered(this.items)),
              spaceBetween: spaceBetween,
            },
          },
        };
      } else {
        additionModules.push(FoxTheme.Swiper.Mousewheel);
        this.settings = {
          mousewheel: {
            enabled: true,
            forceToAxis: true,
            thresholdDelta: 10,
            thresholdTime: 100,
          },
        };
      }

      if (typeof this.sliderInstance !== 'object') {
        this.classList.add(this.classes.swiper);
        this.sliderWrapper.classList.remove(this.classes.grid);
        this.sliderWrapper.classList.add(this.classes.swiperWrapper);
        this.sliderInstance = new window.FoxTheme.Carousel(
          this,
          { ...this.defaultOptions, ...this.settings },
          additionModules
        );
        this.sliderInstance.init();

        // ── A11Y: advance slider when a focusable element inside a slide receives focus
        const focusableElements = FoxTheme.a11y.getFocusableElements(this);
        focusableElements.forEach((element) => {
          element.addEventListener('focusin', () => {
            const slide = element.closest('.swiper-slide');
            if (slide) {
              this.sliderInstance.slider[slideToMethod](
                this.sliderInstance.slider.slides.indexOf(slide)
              );
            }
          });
        });
      }

      if (Shopify.designMode && typeof this.sliderInstance === 'object') {
        document.addEventListener('shopify:block:select', (e) => {
          if (e.detail.sectionId !== this.sectionId) return;
          const index = Number(e.target.dataset.index);
          this.sliderInstance.slider[slideToMethod](index);
        });
      }
    }

    destroySlider() {
      this.classList.remove(this.classes.swiper);
      this.sliderWrapper.classList.remove(this.classes.swiperWrapper);
      this.sliderWrapper.classList.add(this.classes.grid);
      if (typeof this.sliderInstance === 'object') {
        this.sliderInstance.slider.destroy();
        this.sliderInstance = false;
      }
    }

    getSlidesPerViewCentered(number) {
      if (number % 2 !== 0) return number - 1;
      return number;
    }
  }

  customElements.define('testimonials-component', Testimonials);
}

/* ─────────────────────────────────────────────
   TestimonialLayered — fully accessible rewrite
───────────────────────────────────────────── */
if (!customElements.get('testimonial-layered')) {
  /**
   * <testimonial-layered> — expandable testimonial card
   *
   * Expected DOM structure (must be present in Liquid template):
   *
   *   <testimonial-layered data-trigger="click|hover" data-color-scheme="..." data-content-color-scheme="...">
   *     <div class="testimonial__inner color-{scheme}">
   *
   *       <!-- Trigger: ideally a <button>, but we handle a <div> too -->
   *       <div class="testimonial__header">…heading / summary…</div>
   *
   *       <!-- Expandable panel -->
   *       <div class="testimonial__content">
   *         <div class="testimonial__text">…full review text…</div>
   *       </div>
   *
   *     </div>
   *   </testimonial-layered>
   *
   * The component will:
   *   1. Inject the correct ARIA attributes onto existing DOM nodes so the
   *      Liquid template does not need to change for basic functionality.
   *   2. Promote the header to a button role if it is not already a <button>.
   *   3. Manage aria-expanded, aria-controls, aria-hidden, and inert.
   */
  class TestimonialLayered extends HTMLElement {
    /* ── Lifecycle ─────────────────────────────── */

    constructor() {
      super();
      // Bound references stored so they can be properly removed later
      this._onHeaderClick   = this._handleHeaderClick.bind(this);
      this._onKeyDown       = this._handleKeyDown.bind(this);
      this._onDocumentClick = this._handleDocumentClick.bind(this);
      this._onMouseEnter    = this._handleMouseEnter.bind(this);
      this._onMouseLeave    = this._handleMouseLeave.bind(this);
      this._hoverTimeout    = null;
      this._isOpen          = false;
    }

    connectedCallback() {
      this._resolveElements();
      this._setupIds();
      this._setupAriaOnHeader();
      this._setupAriaOnPanel();
      this._applyState(false, /* skipAnimation */ true);
      this._attachEvents();
    }

    disconnectedCallback() {
      this._detachEvents();
      clearTimeout(this._hoverTimeout);
    }

    /* ── Element resolution ────────────────────── */

    _resolveElements() {
      this.inner   = this.querySelector('.testimonial__inner');
      this.header  = this.querySelector('.testimonial__header');
      // .testimonial__summary is the panel — the element that receives
      // aria-hidden/inert and the region role. It wraps all expandable content.
      // .testimonial__content (inner text div) is used only for overflow checks.
      this.content = this.querySelector('.testimonial__summary');
      this.text    = this.querySelector('.testimonial__text');

      if (!this.header || !this.content) {
        console.warn('[testimonial-layered] Required child elements (.testimonial__header, .testimonial__summary) not found.', this);
      }
    }

    /* ── Unique ID helpers ─────────────────────── */

    _setupIds() {
      // Prefer IDs already stamped by Liquid (tl-{blockId}, tl-trigger-{blockId},
      // tl-panel-{blockId}) so ARIA relationships exist before JS upgrades the
      // element. Fall back to generating IDs only if Liquid didn't provide them.
      if (!this.id) {
        const uid = this.dataset.blockId || Math.random().toString(36).slice(2, 8);
        this.id = `tl-${uid}`;
      }

      // Honour a pre-existing header id stamped by Liquid; otherwise derive one.
      const existingTriggerId = this.header && this.header.id;
      this._triggerId = existingTriggerId || `${this.id}-trigger`;

      // Honour a pre-existing panel id stamped by Liquid; otherwise derive one.
      const existingPanelId = this.content && this.content.id;
      this._panelId = existingPanelId || `${this.id}-panel`;
    }

    /* ── ARIA: trigger (header) ────────────────── */

    _setupAriaOnHeader() {
      if (!this.header) return;

      const isNativeButton = this.header.tagName === 'BUTTON';

      if (!isNativeButton) {
        // Liquid pre-wires role/tabindex; these setAttribute calls are a
        // safety net for any layout that doesn't use testimonial-3.liquid.
        if (!this.header.getAttribute('role')) {
          this.header.setAttribute('role', 'button');
        }
        if (!this.header.hasAttribute('tabindex')) {
          this.header.setAttribute('tabindex', '0');
        }
      }

      // Stamp the resolved ID (no-op if Liquid already set the same value).
      this.header.id = this._triggerId;

      // Always ensure aria-expanded is present; Liquid sets 'false' on first
      // paint — we confirm it here in case the element is dynamically inserted.
      if (!this.header.hasAttribute('aria-expanded')) {
        this.header.setAttribute('aria-expanded', 'false');
      }

      // aria-controls: confirm the resolved panel ID is wired correctly.
      this.header.setAttribute('aria-controls', this._panelId);
    }

    /* ── ARIA: content panel ───────────────────── */

    _setupAriaOnPanel() {
      if (!this.content) return;

      // Stamp resolved ID (no-op if Liquid already set the same value).
      this.content.id = this._panelId;

      // Liquid pre-wires role and aria-labelledby; confirm they're present
      // as a safety net for layouts that may not use testimonial-3.liquid.
      if (!this.content.getAttribute('role')) {
        this.content.setAttribute('role', 'region');
      }
      if (!this.content.getAttribute('aria-labelledby')) {
        this.content.setAttribute('aria-labelledby', this._triggerId);
      }
    }

    /* ── State management ──────────────────────── */

    /**
     * _applyState — single source of truth for open/closed state.
     *
     * @param {boolean} open          — target state
     * @param {boolean} skipAnimation — true on first paint to avoid FOUC
     */
    _applyState(open, skipAnimation = false) {
      this._isOpen = open;

      /* ── Trigger ARIA ── */
      if (this.header) {
        this.header.setAttribute('aria-expanded', open ? 'true' : 'false');
      }

      /* ── Panel visibility for AT ──
       *
       * Two-pronged approach for maximum AT compatibility:
       *
       * 1. aria-hidden="true" removes the panel from the AT tree entirely
       *    when collapsed. This prevents screen readers from accidentally
       *    reading hidden content as users navigate through the page.
       *    (VoiceOver, NVDA, JAWS all respect this.)
       *
       * 2. inert (where supported) prevents focus from entering the panel
       *    when collapsed, so Tab key cannot reach hidden interactive
       *    elements. Falls back gracefully in older browsers.
       *
       * WCAG 1.3.1, 2.1.1, 4.1.2
       */
      if (this.content) {
        if (open) {
          this.content.removeAttribute('aria-hidden');
          this.content.removeAttribute('inert');
        } else {
          this.content.setAttribute('aria-hidden', 'true');
          this.content.setAttribute('inert', '');
        }
      }

      /* ── Visual / color state ── */
      this._toggleColorScheme(open);

      /* ── Overflow management ── */
      if (!open) {
        this.content && this.content.classList.remove('overflow-auto');
      }

      /* ── Host attribute for CSS hooks ── */
      if (open) {
        this.setAttribute('open', '');
      } else {
        this.removeAttribute('open');
      }
    }

    /* ── Color scheme toggling ─────────────────── */

    _toggleColorScheme(open) {
      const { colorScheme, contentColorScheme } = this.dataset;
      if (!this.inner || !colorScheme || !contentColorScheme) return;

      // Toggle between the two schemes based on state
      if (open) {
        this.inner.classList.add(contentColorScheme);
        this.inner.classList.remove(colorScheme);
      } else {
        this.inner.classList.remove(contentColorScheme);
        this.inner.classList.add(colorScheme);
      }
    }

    /* ── Open / close helpers ──────────────────── */

    /**
     * Named with underscore prefix to avoid collision with any future
     * HTMLElement.open property or a get/set open() accessor pair.
     * These are the canonical internal action methods.
     */
    _open() {
      if (this._isOpen) return;
      this._applyState(true);
    }

    _close() {
      if (!this._isOpen) return;
      this._applyState(false);

      /**
       * Return focus to the trigger when closing via Escape or outside click.
       * WCAG 2.4.3 — Focus Order: focus must move to a logical place.
       */
      if (document.activeElement !== this.header && this.contains(document.activeElement)) {
        this.header && this.header.focus();
      }
    }

    _toggle() {
      this._isOpen ? this._close() : this._open();
    }

    /* ── Event attachment ──────────────────────── */

    _attachEvents() {
      if (!this.header) return;

      // Click: works for both mouse users and screen reader virtual cursor
      this.header.addEventListener('click', this._onHeaderClick);

      /**
       * Keyboard: native <button> gets Enter/Space for free.
       * For promoted-div triggers we must handle it manually.
       * We handle it for both cases for safety and Escape support.
       *
       * WCAG 2.1.1 — Keyboard: all functionality must be keyboard operable.
       */
      this.header.addEventListener('keydown', this._onKeyDown);

      /**
       * Outside-click to close — paired with Escape (see _handleKeyDown).
       * We attach to document but guard with contains() check.
       */
      document.addEventListener('click', this._onDocumentClick, { passive: true });

      /**
       * Hover mode is ADDITIVE:
       * It only activates when the device supports hover (pointer device).
       * Keyboard and touch users are completely unaffected.
       *
       * WCAG 1.3.3 — Sensory Characteristics
       * WCAG 2.1.1 — Keyboard
       */
      if (this.trigger === 'hover' && window.matchMedia('(hover: hover)').matches) {
        this.addEventListener('mouseenter', this._onMouseEnter);
        this.addEventListener('mouseleave', this._onMouseLeave);
      }
    }

    _detachEvents() {
      this.header && this.header.removeEventListener('click', this._onHeaderClick);
      this.header && this.header.removeEventListener('keydown', this._onKeyDown);
      document.removeEventListener('click', this._onDocumentClick);
      this.removeEventListener('mouseenter', this._onMouseEnter);
      this.removeEventListener('mouseleave', this._onMouseLeave);
    }

    /* ── Event handlers ────────────────────────── */

    _handleHeaderClick(event) {
      event.preventDefault();
      this._toggle();
    }

    /**
     * _handleKeyDown
     *
     * Handles:
     *   Enter / Space → toggle (required for div-as-button, harmless on native button)
     *   Escape        → close and return focus to trigger
     *
     * WCAG 2.1.1 — all pointer functionality must have a keyboard equivalent.
     */
    _handleKeyDown(event) {
      switch (event.key) {
        case 'Enter':
        case ' ':
          // Only intercept for non-native-button triggers to avoid double-fire
          if (this.header.tagName !== 'BUTTON') {
            event.preventDefault();
            this._toggle();
          }
          break;

        case 'Escape':
          if (this._isOpen) {
            event.stopPropagation();
            this._close();
          }
          break;
      }
    }

    /**
     * _handleDocumentClick
     *
     * Closes the card when the user clicks outside of it.
     * Uses composedPath() to work correctly inside Shadow DOM if ever needed.
     */
    _handleDocumentClick(event) {
      if (!this._isOpen) return;
      const path = event.composedPath ? event.composedPath() : [event.target];
      if (!path.includes(this.inner || this)) {
        this._close();
      }
    }

    /**
     * _handleMouseEnter / _handleMouseLeave
     *
     * Hover open/close for pointer devices only.
     * A short timeout before adding overflow-auto prevents jarring reflow
     * mid-animation, but ARIA state updates are immediate — never delayed.
     */
    _handleMouseEnter() {
      clearTimeout(this._hoverTimeout);
      this._open();

      // Deferred overflow — only a visual concern, never blocks AT state
      this._hoverTimeout = setTimeout(() => {
        if (this.text && this.content) {
          const textH    = this.text.getBoundingClientRect().height;
          const contentH = this.content.getBoundingClientRect().height;
          if (textH > contentH) {
            this.content.classList.add('overflow-auto');
          }
        }
      }, 600);
    }

    _handleMouseLeave() {
      clearTimeout(this._hoverTimeout);
      this._close();
      this.content && this.content.classList.remove('overflow-auto');
    }

    /* ── Getters ───────────────────────────────── */

    get trigger() {
      return this.getAttribute('data-trigger') || 'click';
    }

    /**
     * isOpen — read-only public accessor for external consumers.
     * Internal code always reads this._isOpen directly.
     */
    get isOpen() {
      return this._isOpen;
    }
  }

  customElements.define('testimonial-layered', TestimonialLayered);
}
